import { Input, Progress, Segmented, Select, Table, Tag, Typography } from "antd";
import GlassPanel from "../../components/common/glassPanel";

const { Text, Title } = Typography;

const stats: Array<{ label: string; value: string; trend: string; tone: "positive" | "attention" }> = [
  { label: "Live Listings", value: "1,284", trend: "+8.4% this week", tone: "positive" },
  { label: "Active Dealers", value: "116", trend: "6 pending verification", tone: "attention" },
  { label: "Gross Volume", value: "KES 92.4M", trend: "+14.2% month to date", tone: "positive" },
  { label: "Open Incidents", value: "18", trend: "4 breaching SLA", tone: "attention" },
];

const queue = [
  { key: "1", item: "Dealer payout hold", area: "Finance Ops", detail: "3 settlements blocked by ownership mismatch.", priority: "Critical", age: "2h" },
  { key: "2", item: "Auction moderation", area: "Trust & Safety", detail: "12 units flagged for duplicate media.", priority: "High", age: "46m" },
  { key: "3", item: "Inspection backlog", area: "Field Ops", detail: "Nairobi East exceeds 24h target.", priority: "Normal", age: "5h" },
];

const markets = [
  { key: "1", market: "Nairobi", status: "Stable", dealers: 41, volume: "KES 38.7M" },
  { key: "2", market: "Mombasa", status: "Growing", dealers: 15, volume: "KES 11.4M" },
  { key: "3", market: "Kisumu", status: "Watch", dealers: 8, volume: "KES 6.9M" },
];

const pipeline = [
  { stage: "Onboarding", count: 14, serviceLevel: "Avg 1.2 days" },
  { stage: "Listing review", count: 37, serviceLevel: "Avg 46 min" },
  { stage: "Settlement", count: 9, serviceLevel: "Avg 6.4 h" },
];

function toneClassName(tone: "positive" | "attention") {
  return tone === "positive" ? "text-emerald-500" : "text-amber-500";
}

export default function WheelsDashboardScreen() {
  return (
    <div className="space-y-4 px-1 py-1">
      <GlassPanel
        eyebrow="Wheels"
        title="Marketplace Dashboard"
        actions={
          <div className="flex flex-wrap gap-2">
            <Segmented options={["Today", "7 Days", "30 Days"]} defaultValue="7 Days" />
            <Select
              defaultValue="all"
              options={[
                { value: "all", label: "All regions" },
                { value: "nairobi", label: "Nairobi" },
                { value: "coast", label: "Coast" },
              ]}
              style={{ width: 140 }}
            />
            <Input.Search placeholder="Search vehicle, dealer, or ticket" style={{ width: 260 }} />
          </div>
        }
      >
        <Text type="secondary">
          Use this page for marketplace-wide monitoring. Summary cards stay shallow; operational follow-up happens in tables below.
        </Text>
      </GlassPanel>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {stats.map((stat) => (
          <GlassPanel key={stat.label} bodyClassName="py-4">
            <Text className="block text-[11px]! uppercase tracking-[0.18em] text-black/45 dark:text-white/45">{stat.label}</Text>
            <Title level={4} className="mb-1! mt-2!">{stat.value}</Title>
            <Text className={toneClassName(stat.tone)}>{stat.trend}</Text>
          </GlassPanel>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1.1fr_0.9fr]">
        <GlassPanel eyebrow="Attention" title="Priority Queue">
          <Table
            rowKey="key"
            size="small"
            pagination={false}
            dataSource={queue}
            columns={[
              { title: "Item", dataIndex: "item" },
              { title: "Area", dataIndex: "area" },
              {
                title: "Priority",
                dataIndex: "priority",
                render: (value: string) => <Tag color={value === "Critical" ? "error" : value === "High" ? "warning" : "default"}>{value}</Tag>,
              },
              { title: "Age", dataIndex: "age" },
            ]}
            expandable={{
              expandedRowRender: (record) => <Text type="secondary">{record.detail}</Text>,
            }}
          />
        </GlassPanel>

        <GlassPanel eyebrow="Regional Readout" title="Market Coverage">
          <Table
            rowKey="key"
            size="small"
            pagination={false}
            dataSource={markets}
            columns={[
              { title: "Market", dataIndex: "market" },
              {
                title: "Status",
                dataIndex: "status",
                render: (value: string) => <Tag color={value === "Stable" ? "success" : value === "Growing" ? "processing" : "warning"}>{value}</Tag>,
              },
              { title: "Dealers", dataIndex: "dealers" },
              { title: "Volume", dataIndex: "volume" },
            ]}
          />
        </GlassPanel>
      </div>

      <GlassPanel eyebrow="Pipeline" title="Operational Flow">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
          {pipeline.map((item) => (
            <div key={item.stage} className="rounded-md border border-black/8 bg-black/[0.02] p-4 dark:border-white/8 dark:bg-white/[0.03]">
              <Text className="block text-[11px]! uppercase tracking-[0.18em] text-black/45 dark:text-white/45">{item.stage}</Text>
              <Title level={4} className="mb-0! mt-2!">{item.count}</Title>
              <Progress percent={Math.min(item.count * 4, 100)} showInfo={false} strokeColor="#00e5ff" />
              <Text type="secondary">{item.serviceLevel}</Text>
            </div>
          ))}
        </div>
      </GlassPanel>
    </div>
  );
}
